import React, { useCallback, useState, useEffect } from 'react';
import * as Yup from 'yup';
import { ErrorMessage, Field, Form, Formik } from 'formik';
import {useDispatch} from 'react-redux';
import {showModal} from '../../../../redux/system/actions';
import ConfirmModal from '../../../common/modals/ConfirmModal';

const CANCEL_MODAL_ID = 'cancelModal';

const validationSchema = Yup.object().shape({
  studyField: Yup.string().trim().required('Study field is required'),
  institution: Yup.string().trim().required('Institution is required'),
  town: Yup.string().trim().required('Town is required'),
  startMonth: Yup.number().required('Start month is required').min(1, 'Must be between 1 and 12').max(12, 'Must be between 1 and 12'),
  startYear: Yup.number()
  .required('Start year is required')
  .test('not-in-future', 'Start date cannot be in the future', function (startYear) {
    const { startMonth } = this.parent;
    if (!startYear || !startMonth) return true;

    const selectedDate = new Date(startYear, startMonth - 1);
    const today = new Date();
    selectedDate.setHours(0, 0, 0, 0);
    today.setHours(0, 0, 0, 0);

    return selectedDate <= today;
  }),  
  endMonth: Yup.number().when('currentStudy', {
    is: false,
    then: (schema) => schema.required('End month is required'),
    otherwise: (schema) => schema,
  }),
  endYear: Yup.number().when('currentStudy', {
    is: false,
    then: (schema) => schema.required('End year is required'),
    otherwise: (schema) => schema,
  }),
  currentStudy: Yup.boolean(),
  degree: Yup.string().required('Degree is required'),
});

const months = [
  { value: 1, label: 'January' },
  { value: 2, label: 'February' },
  { value: 3, label: 'March' },
  { value: 4, label: 'April' },
  { value: 5, label: 'May' },
  { value: 6, label: 'June' },
  { value: 7, label: 'July' },
  { value: 8, label: 'August' },
  { value: 9, label: 'September' },
  { value: 10, label: 'October' },
  { value: 11, label: 'November' },
  { value: 12, label: 'December' },
];

const years = Array.from({ length: 50 }, (_, i) => ({
  value: new Date().getFullYear() - i,
  label: new Date().getFullYear() - i,
}));

function EducationForm({ curriculumVitaeId, initialEducation, setEditingEducation, setAddingEducation, onSave, status }) {
  const dispatch = useDispatch();
  const handleShowModal = useCallback((modalId) => dispatch(showModal(modalId)), [dispatch]);

  const [education, setEducation] = useState(
    initialEducation || {
      studyField: '',
      institution: '',
      town: '',
      startMonth: '',
      startYear: '',
      startDate: '',
      endMonth: '',
      endYear: '',
      endDate: '',
      currentStudy: false,
      degree: '',
      curriculumVitaeId,
    },
  );

  const clearForm = () => {
    setEditingEducation(null);

    setEducation({
      studyField: '',
      institution: '',
      town: '',
      startMonth: '',
      startYear: '',
      startDate: '',
      endMonth: '',
      endYear: '',
      endDate: '',
      currentStudy: false,
      degree: '',
      curriculumVitaeId,
    });
  };

  const formatDate = (year, month) => {
    if (!year || !month) return null;
    const date = new Date(year, month);
    return date.toISOString().split('T')[0];
  };

  const handleCancel = () => {
    handleShowModal(CANCEL_MODAL_ID);
  };

  const confirmCancel = () => {
    setAddingEducation(false);
    setEditingEducation(null);
  };

  useEffect(() => {
    if (initialEducation) {
      const startDate = initialEducation.startDate ? new Date(initialEducation.startDate) : null;
      const endDate = initialEducation.endDate ? new Date(initialEducation.endDate) : null;

      const startMonth = startDate ? startDate.getMonth() + 1 : '';
      const startYear = startDate ? startDate.getFullYear() : '';
      const endMonth = endDate ? endDate.getMonth() + 1 : '';
      const endYear = endDate ? endDate.getFullYear() : '';

      setEducation({
        ...initialEducation,
        startMonth: startMonth,
        startYear: startYear,
        endMonth: endMonth,
        endYear: endYear,
      });
    }
  }, [initialEducation]);

  return (
    <><Formik
      enableReinitialize={true}
      initialValues={education}
      validationSchema={validationSchema}
      onSubmit={(values, { resetForm }) => {
        const updatedEducation = {
          ...values,
          startDate: formatDate(values.startYear, values.startMonth),
          endDate: values.currentStudy ? null : formatDate(values.endYear, values.endMonth),
        };

        // TODO: TEMP FIX (we need to validate this in our schema)
        if (updatedEducation.endDate) {
          const start = new Date(updatedEducation.startDate);
          const end = new Date(updatedEducation.endDate);

          if (end < start) {
            alert('End date cannot be earlier than start date');
            return;
          }
        }

        onSave(updatedEducation);
        clearForm();
        resetForm();
      } }>
      {({ values }) => (
        <Form>
          <div className="row">
            <div className="col-md-4">
              <div className="form-group">
                <label htmlFor="studyField" className="small">
                  STUDY FIELD *
                </label>
                <Field name="studyField">
                  {({ field, meta }) => (
                    <input
                      {...field}
                      type="text"
                      className={`form-control ${meta.touched && meta.error ? 'is-invalid' : ''}`}
                      placeholder="Enter study field"
                    />
                  )}
                </Field>                 
                <ErrorMessage name="studyField">
                  {(msg) => (
                    <div className="text-danger mt-1" style={{ fontSize: '0.875rem', fontStyle: 'italic' }}>
                      {msg}
                    </div>
                  )}
                </ErrorMessage>
              </div>
            </div>

            <div className="col-md-4">
              <div className="form-group">
                <label htmlFor="institution" className="small">
                  INSTITUTION *
                </label>
                <Field name="institution">
                  {({ field, meta }) => (
                    <input
                      {...field}
                      type="text"
                      className={`form-control ${meta.touched && meta.error ? 'is-invalid' : ''}`}
                      placeholder="Enter institution"
                    />
                  )}
                </Field>                
                <ErrorMessage name="institution">
                  {(msg) => (
                    <div className="text-danger mt-1" style={{ fontSize: '0.875rem', fontStyle: 'italic' }}>
                      {msg}
                    </div>
                  )}
                </ErrorMessage>
              </div>
            </div>

            <div className="col-md-4">
              <div className="form-group">
                <label htmlFor="town" className="small">
                  TOWN *
                </label>
                <Field name="town">
                  {({ field, meta }) => (
                    <input
                      {...field}
                      type="text"
                      className={`form-control ${meta.touched && meta.error ? 'is-invalid' : ''}`}
                      placeholder="Enter town"
                    />
                  )}
                </Field>                 
                <ErrorMessage name="town">
                  {(msg) => (
                    <div className="text-danger mt-1" style={{ fontSize: '0.875rem', fontStyle: 'italic' }}>
                      {msg}
                    </div>
                  )}
                </ErrorMessage>
              </div>
            </div>
          </div>

          <div className="row">
            <div className="col-md-4">
              <div className="form-group">
                <label htmlFor="startDate" className="small">
                  START DATE *
                </label>
                <div className="d-flex">
                  <div className="mr-2" style={{ flex: '1' }}>
                  <Field name="startMonth">
                    {({ field, meta }) => (
                      <select
                        {...field}
                        className={`form-control ${meta.touched && meta.error ? 'is-invalid' : ''}`}
                      >
                        <option value="">Month</option>
                        {months.map((month) => (
                          <option key={month.value} value={month.value}>
                            {month.label}
                          </option>
                        ))}
                      </select>
                    )}
                  </Field>
                    <ErrorMessage name="startMonth">
                      {(msg) => (
                        <div className="text-danger mt-1" style={{ fontSize: '0.875rem', fontStyle: 'italic' }}>
                          {msg}
                        </div>
                      )}
                    </ErrorMessage>
                  </div>

                  <div style={{ flex: '1' }}>
                  <Field name="startYear">
                    {({ field, meta }) => (
                      <select
                        {...field}
                        className={`form-control ${meta.touched && meta.error ? 'is-invalid' : ''}`}
                      >
                        <option value="">Year</option>
                        {years.map((year) => (
                          <option key={year.value} value={year.value}>
                            {year.label}
                          </option>
                        ))}
                      </select>
                    )}
                  </Field>
                    <ErrorMessage name="startYear">
                      {(msg) => (
                        <div className="text-danger mt-1" style={{ fontSize: '0.875rem', fontStyle: 'italic' }}>
                          {msg}
                        </div>
                      )}
                    </ErrorMessage>
                  </div>
                </div>
              </div>
            </div>

            <div className="col-md-4">
              <div className="form-group">
                <label htmlFor="endDate" className="small">
                  END DATE *
                </label>
                <div className="d-flex">
                  <div className="mr-2" style={{ flex: '1' }}>
                  <Field name="endMonth">
                    {({ field, meta }) => (
                      <select
                        {...field}
                        disabled={values.currentStudy}
                        className={`form-control ${meta.touched && meta.error ? 'is-invalid' : ''}`}
                      >
                        <option value="">Month</option>
                        {months.map((month) => (
                          <option key={month.value} value={month.value}>
                            {month.label}
                          </option>
                        ))}
                      </select>
                    )}
                  </Field>
                    <ErrorMessage name="endMonth">
                      {(msg) => (
                        <div className="text-danger mt-1" style={{ fontSize: '0.875rem', fontStyle: 'italic' }}>
                          {msg}
                        </div>
                      )}
                    </ErrorMessage>
                  </div>

                  <div style={{ flex: '1' }}>
                  <Field name="endYear" disabled={values.currentStudy}>
                    {({ field, meta }) => (
                      <select
                        {...field}
                        disabled={values.currentStudy}
                        className={`form-control ${meta.touched && meta.error ? 'is-invalid' : ''}`}
                      >
                        <option value="">Year</option>
                        {years.map((year) => (
                          <option key={year.value} value={year.value}>
                            {year.label}
                          </option>
                        ))}
                      </select>
                    )}
                  </Field>
                    <ErrorMessage name="endYear">
                      {(msg) => (
                        <div className="text-danger mt-1" style={{ fontSize: '0.875rem', fontStyle: 'italic' }}>
                          {msg}
                        </div>
                      )}
                    </ErrorMessage>
                  </div>
                </div>
              </div>
            </div>

            <div className="col-md-4 d-flex align-items-center">
              <div className="form-group mb-0 d-flex align-items-center">
                <Field type="checkbox" name="currentStudy" className="form-check-input m-0" />
                <label htmlFor="currentStudy" className="small mt-0 mb-0 ml-4">
                  Current study
                </label>
              </div>
            </div>
          </div>

          <div className="row">
            <div className="col-md-4">
              <div className="form-group">
                <label htmlFor="degree" className="small">
                  DEGREE *
                </label>
                <Field id="degree" name="degree">
                  {({ field, meta }) => (
                    <select 
                    {...field}
                    className={`form-control ${meta.touched && meta.error ? 'is-invalid' : ''}`}                    
                    >
                  <option disabled value="">
                    Select a degree
                  </option>
                  <option value="Bachelor">Bachelor</option>
                  <option value="Master">Master</option>
                  <option value="Graduate">Graduate</option>
                  <option value="Training">Training</option>
                  <option value="High school">High school</option>
                  <option value="ManaMa">ManaMa</option>
                  <option value="BanaBa">BanaBa</option>
                    </select>
                  )}
                </Field>
                <ErrorMessage name="degree">
                  {(msg) => (
                    <div className="text-danger mt-1" style={{ fontSize: '0.875rem', fontStyle: 'italic' }}>
                      {msg}
                    </div>
                  )}
                </ErrorMessage>
              </div>
            </div>
          </div>

          <div className="mb-5">
            {initialEducation ? (
              <div className="d-flex align-items-start">
                <button className="btn btn-primary d-flex align-items-center mt-2 mr-3" type="submit">
                  <i className="pe-7s-pen mr-2"></i>
                  <span>Update Education</span>
                </button>
                {status === 1 ? (
                  <button className="cancel-button d-flex mt-2" type="button" onClick={handleCancel}>
                    <span>Cancel</span>
                  </button>
                ) : (
                  <button className="cancel-button d-flex align-items-center mt-2" type="button" onClick={clearForm}>
                    <span>Cancel</span>
                  </button>
                )}
              </div>
            ) : (
              <div className='d-flex'>
                <button className="btn btn-primary d-flex mt-2 mr-3" type="submit">
                  <i className="pe-7s-plus mr-2"></i>
                  <span>Add Education</span>
                </button>
                {status === 1 && (
                  <button className="cancel-button d-flex mt-2" type="button" onClick={handleCancel}>
                    <span>Cancel</span>
                  </button>
                )}
              </div>
            )}
          </div>
        </Form>
      )}
    </Formik>
    <ConfirmModal
        id={CANCEL_MODAL_ID}
        title="Are you sure you want to stop adding this education?"
        message="This will cancel the form and return you to the overview. Do you wish to proceed?"
        onConfirm={confirmCancel} />
    </>
  );
}

export default EducationForm;
